import { doAddEvent, doRemoveEvents } from 'lib/events';
import { theme } from 'lib/theme';
import { random } from 'lodash';
import * as React from 'react';
import { useDispatch } from 'react-redux';
import { CONFIRM } from 'store/app/actions';
import { Confirm } from 'store/app/types';
import { createAction } from 'typesafe-actions';

type ConfirmHook = Omit<Confirm, 'onCancel' | 'onConfirm' | 'onAltConfirm'> & {
  onCancel?: () => void;
  onConfirm?: () => void;
  onAltConfirm?: () => void;
  image?: string | undefined;
};

type HandleConfirm = (props: ConfirmHook) => void;

export const useConfirm = (): HandleConfirm => {
  const dispatch = useDispatch();
  const keyRef = React.useRef<string>(`-confirm-${random(1, 999999)}`);

  const cancelType = React.useMemo(
    () => createAction(`USE_CONFIRM_CANCEL_ACTION${keyRef.current}`)(),
    [],
  );
  const confirmType = React.useMemo(
    () => createAction(`USE_CONFIRM_CONFIRM_ACTION${keyRef.current}`)(),
    [],
  );
  const altConfirmType = React.useMemo(
    () => createAction(`USE_CONFIRM_ALT_CONFIRM_ACTION${keyRef.current}`)(),
    [],
  );

  const onRef = React.useRef<{
    cancel: () => void;
    confirm: () => void;
    altConfirm: () => void;
  }>(null);

  React.useEffect(() => {
    const events = [
      doAddEvent(cancelType, () => onRef.current?.cancel()),
      doAddEvent(confirmType, () => onRef.current?.confirm()),
      doAddEvent(altConfirmType, () => onRef.current?.altConfirm()),
    ];
    return () => {
      doRemoveEvents(events);
    };
  }, [cancelType, confirmType, altConfirmType]);

  return React.useCallback(
    (props) => {
      onRef.current = {
        cancel: () => props.onCancel?.(),
        confirm: () => props.onConfirm?.(),
        altConfirm: () => props.onAltConfirm?.(),
      };
      dispatch(
        CONFIRM({
          ...props,
          onCancel: cancelType(),
          onConfirm: confirmType(),
          onAltConfirm: props.onAltConfirm ? altConfirmType() : undefined,
        }),
      );
    },
    [altConfirmType, cancelType, confirmType, dispatch],
  );
};

type ConfirmUnsavedHook = {
  text?: string;
  onCancel?: () => void;
  onDiscard?: () => void;
  onSave?: () => void;
  onSaveText?: string;
  onDiscardText?: string;
};

type HandleConfirmUnsaved = (props: ConfirmUnsavedHook) => void;

export const useConfirmUnsaved = (): HandleConfirmUnsaved => {
  const dispatch = useDispatch();
  const keyRef = React.useRef<string>(`-confirm-${random(1, 999999)}`);

  const cancelType = React.useMemo(
    () => createAction(`USE_CONFIRM_CANCEL_ACTION${keyRef.current}`)(),
    [],
  );
  const confirmType = React.useMemo(
    () => createAction(`USE_CONFIRM_CONFIRM_ACTION${keyRef.current}`)(),
    [],
  );
  const altConfirmType = React.useMemo(
    () => createAction(`USE_CONFIRM_ALT_CONFIRM_ACTION${keyRef.current}`)(),
    [],
  );

  const onRef = React.useRef<{
    cancel: () => void;
    confirm: () => void;
    altConfirm: () => void;
  }>(null);

  React.useEffect(() => {
    const events = [
      doAddEvent(cancelType, () => onRef.current?.cancel()),
      doAddEvent(confirmType, () => onRef.current?.confirm()),
      doAddEvent(altConfirmType, () => onRef.current?.altConfirm()),
    ];
    return () => {
      doRemoveEvents(events);
    };
  }, [cancelType, confirmType, altConfirmType]);

  return React.useCallback(
    (props) => {
      onRef.current = {
        cancel: () => props.onCancel?.(),
        confirm: () => props.onDiscard?.(),
        altConfirm: () => props.onSave?.(),
      };
      dispatch(
        CONFIRM({
          text: props.text || 'You have unsaved changes',
          destructive: true,
          altColor: theme.colors.green,
          confirmText: props.onDiscardText || 'Discard Changes',
          altConfirmText: props.onSaveText || 'Save Changes',
          cancelText: 'Cancel',
          onCancel: cancelType(),
          onConfirm: confirmType(),
          onAltConfirm: altConfirmType(),
        }),
      );
    },
    [altConfirmType, cancelType, confirmType, dispatch],
  );
};
